/**
 * Ticket writes shared by the support inbox and the Brain deck's ticket cards.
 * Callers own their toasts and query invalidation. Assignment is not here: it
 * goes through assignTicket (src/lib/ticket-assignment.ts).
 */
import { supabase } from "@/lib/supabase";
import { adminDisplayName } from "@/hooks/useAdminRoster";
import { OPEN_STATUSES } from "@/lib/support-views";
import type { AssignTicketResult } from "@/lib/ticket-assignment";
import type { Database } from "@/lib/supabase-types";
import { encodeStructuredSupportEvent } from "@/lib/support-system-events";

type IssueUpdate = Database["public"]["Tables"]["issues"]["Update"];

/** The matchesView trap fix (see SupportInbox.tsx's file header), isolated as
 *  a pure function so the decision is unit-testable without rendering anything
 *  or mocking supabase. */
export function computeStatusPatch(status: string): IssueUpdate {
  const patch: IssueUpdate = { status };
  if (status === "resolved") {
    patch.resolved_at = new Date().toISOString();
    patch.archived_at = new Date().toISOString();
  } else if (OPEN_STATUSES.includes(status)) {
    patch.archived_at = null;
    // A reopened ticket must not keep a stale resolution stamp from a prior
    // resolve/reopen cycle — nothing reads this today, but the UI advertises
    // reopen-from-Archived as a supported round trip.
    patch.resolved_at = null;
  }
  return patch;
}

/** The old console's exact synthetic assignment comment, reproduced as a
 *  pure function so the preserved text is unit-testable directly. */
export function buildAssignComment(
  userId: string | null,
  roster: Array<{ id: string; name: string | null; email: string | null }>,
  t: (key: string, fallback?: string) => string,
  actorId?: string | null
): string {
  const actor = adminDisplayName(
    roster.find((person) => person.id === actorId),
    t
  );
  if (!userId)
    return encodeStructuredSupportEvent({
      type: "assignment.unassigned",
      actor,
    });
  const assignee = roster.find((a) => a.id === userId);
  const assigneeName = adminDisplayName(assignee, t);
  return userId === actorId
    ? encodeStructuredSupportEvent({ type: "assignment.taken", actor })
    : encodeStructuredSupportEvent({
        type: "assignment.handover",
        actor,
        assignee: assigneeName,
      });
}

/**
 * What did not land after an assignment that DID commit, as warning toasts.
 * Empty means everything the admin asked for happened. Pure, so the wording
 * and the placeholder handling are testable without rendering the inbox.
 */
export function assignWarnings(
  result: Pick<
    AssignTicketResult,
    "systemCommentFailed" | "noteFailed" | "email"
  >,
  assigneeName: string,
  t: (key: string, fallback?: string) => string
): string[] {
  const warnings: string[] = [];
  if (result.systemCommentFailed) {
    warnings.push(
      t(
        "support.detail.assignCommentFailed",
        "Assignee updated, but the assignment note failed to post"
      )
    );
  }
  if (result.noteFailed) {
    warnings.push(
      t(
        "support.detail.assignNoteFailed",
        "Assignee updated, but your note could not be saved"
      )
    );
  }
  void assigneeName;
  return warnings;
}

export async function archiveTickets(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const { error } = await supabase
    .from("issues")
    .update({ archived_at: new Date().toISOString() })
    .in("id", ids);
  if (error) throw error;
}

export async function setTicketStatus(
  ticketId: string,
  status: string
): Promise<void> {
  const { error } = await supabase
    .from("issues")
    .update(computeStatusPatch(status))
    .eq("id", ticketId);
  if (error) throw error;
}
